import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBTooltip,
} from "mdb-react-ui-kit";
import { ChevronDown } from "lucide-react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { api } from "../services";
import { buildDashboardApiParams } from "../utils/dashboardApiParams";
import { useTranslation } from "react-i18next";

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Anchor just outside the full donut (incl. gray track), along the ray from
 * center through the hover point — keeps the bubble near the cursor and off the ring.
 */
const getOutsideTooltipAnchor = (chart, tooltip, gap = 10) => {
  const area = chart.chartArea;
  if (!area || area.width <= 0 || area.height <= 0) return null;

  const cx = (area.left + area.right) / 2;
  const cy = (area.top + area.bottom) / 2;
  const fullOuter = Math.min(area.width, area.height) / 2;

  let dx = (tooltip.caretX ?? cx) - cx;
  let dy = (tooltip.caretY ?? cy) - cy;
  const dist = Math.hypot(dx, dy);
  if (dist < 1) {
    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;
    const arc = chart.getDatasetMeta(0)?.data?.[dataIndex];
    if (arc) {
      const angle = (arc.startAngle + arc.endAngle) / 2;
      dx = Math.cos(angle);
      dy = Math.sin(angle);
    } else {
      dx = 1;
      dy = 0;
    }
  } else {
    dx /= dist;
    dy /= dist;
  }

  const radius = fullOuter + gap;
  let side;
  if (Math.abs(dx) >= Math.abs(dy)) {
    side = dx >= 0 ? "right" : "left";
  } else {
    side = dy >= 0 ? "bottom" : "top";
  }

  return {
    x: cx + dx * radius,
    y: cy + dy * radius,
    side,
  };
};

const ensureInventoryTooltipEl = (tooltipRef) => {
  let el = tooltipRef.current;
  if (el && document.body.contains(el)) return el;

  el = document.createElement("div");
  el.className = "oh-inventory-tooltip oh-inventory-tooltip--portal";
  el.setAttribute("role", "tooltip");
  el.innerHTML = `
    <div class="oh-inventory-tooltip__caret" aria-hidden="true"></div>
    <div class="oh-inventory-tooltip__card">
      <span class="oh-inventory-tooltip__text"></span>
    </div>
  `;
  el.style.opacity = "0";
  el.style.pointerEvents = "none";
  document.body.appendChild(el);
  tooltipRef.current = el;
  return el;
};

/** Figma 1:2752 Inventory */
const COLORS = {
  inStock: "#083B4C",
  lowStock: "#FF9B53",
  outOfStock: "#FFC7A0",
  track: "rgba(232, 232, 232, 0.5)",
};

/** Stable plugin id — avoid re-registering a new object every render. */
const inventoryTrackPlugin = {
  id: "inventoryTrack",
  beforeDatasetsDraw(chart) {
    const arc = chart.getDatasetMeta(0)?.data?.[0];
    const chartArea = chart.chartArea;
    if (!arc || !chartArea) return;
    const { ctx } = chart;
    const { top, left, width, height } = chartArea;
    const x = width / 2 + left;
    const y = height / 2 + top;
    const fullOuter = Math.min(width, height) / 2;
    const inner = arc.innerRadius;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, fullOuter, 0, Math.PI * 2, false);
    ctx.arc(x, y, inner, 0, Math.PI * 2, true);
    ctx.fillStyle = COLORS.track;
    ctx.fill();
    ctx.restore();
  },
};

const InventoryCard = ({ brandId, branchId, categoryId }) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const tooltipRef = useRef(null);
  const chartRef = useRef(null);
  const [inventoryData, setInventoryData] = useState({
    inStock: 0,
    lowStock: 0,
    outOfStock: 0,
    totalStock: 0,
  });
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/dashboard/inventory-analytics", {
          headers: { Authorization: `Bearer ${token}` },
          params: buildDashboardApiParams({ brandId, branchId, categoryId }),
        });
        if (cancelled) return;
        const payload = response?.data?.data || response?.data || {};
        const summary = payload?.summary || {};
        const num = (v) => (typeof v === "number" ? v : Number(v) || 0);
        setInventoryData({
          inStock: num(summary.inStock ?? 0),
          lowStock: num(summary.lowStock ?? 0),
          outOfStock: num(summary.outOfStock ?? 0),
          totalStock: num(summary.totalStock ?? 0),
        });
      } catch (err) {
        if (cancelled) return;
        setInventoryData({
          inStock: 0,
          lowStock: 0,
          outOfStock: 0,
          totalStock: 0,
        });
      } finally {
        if (!cancelled) setHasLoaded(true);
      }
    };
    setHasLoaded(false);
    fetchAnalytics();
    return () => {
      cancelled = true;
    };
  }, [brandId, branchId, categoryId]);

  useEffect(() => {
    return () => {
      const el = tooltipRef.current;
      if (el?.parentNode) {
        el.parentNode.removeChild(el);
      }
      tooltipRef.current = null;
    };
  }, []);

  // After data arrives (or layout settles), force Chart.js to remeasure hit areas.
  useEffect(() => {
    if (!hasLoaded) return undefined;
    const id = requestAnimationFrame(() => {
      const chart = chartRef.current;
      if (chart && typeof chart.resize === "function") {
        chart.resize();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [hasLoaded, inventoryData.inStock, inventoryData.lowStock, inventoryData.outOfStock]);

  const totalStock =
    typeof inventoryData.totalStock === "number" && inventoryData.totalStock > 0
      ? inventoryData.totalStock
      : inventoryData.inStock +
        inventoryData.lowStock +
        inventoryData.outOfStock;

  const labelText = useMemo(
    () => ({
      inStock: isArabic ? "متوفر" : "In Stock",
      lowStock: isArabic ? "مخزون منخفض" : "Low in stock",
      outOfStock: isArabic ? "غير متوفر" : "Out of Stock",
      totalStock: isArabic ? "إجمالي المخزون" : "Total Stock",
    }),
    [isArabic]
  );

  const values = useMemo(
    () => [
      inventoryData.inStock,
      inventoryData.lowStock,
      inventoryData.outOfStock,
    ],
    [inventoryData.inStock, inventoryData.lowStock, inventoryData.outOfStock]
  );

  const categoryLabels = useMemo(
    () => [labelText.inStock, labelText.lowStock, labelText.outOfStock],
    [labelText]
  );

  /**
   * Zero categories render as a ~2px tick (not a fat slice). Chart.js drops
   * 0-value arcs, so we keep a hairline stand-in; tooltips still show 0.
   * When every count is 0, ticks sit on the gray track with transparent gaps.
   */
  const donutModel = useMemo(() => {
    const colors = [COLORS.inStock, COLORS.lowStock, COLORS.outOfStock];
    const sum = values.reduce((a, b) => a + b, 0);
    const lineRatio = 1.6 / 360; // ~2px on the 170px ring

    const data = [];
    const backgroundColor = [];
    const labels = [];
    const realIndex = [];

    if (sum <= 0) {
      const gap = (1 - lineRatio * 3) / 3;
      values.forEach((_, i) => {
        data.push(lineRatio);
        backgroundColor.push(colors[i]);
        labels.push(categoryLabels[i]);
        realIndex.push(i);

        data.push(gap);
        backgroundColor.push("rgba(0,0,0,0)");
        labels.push("");
        realIndex.push(-1);
      });
      return { data, backgroundColor, labels, realIndex };
    }

    const lineWeight = sum * lineRatio;
    values.forEach((v, i) => {
      data.push(v > 0 ? v : lineWeight);
      backgroundColor.push(colors[i]);
      labels.push(categoryLabels[i]);
      realIndex.push(i);
    });
    return { data, backgroundColor, labels, realIndex };
  }, [values, categoryLabels]);

  /**
   * Figma Group 2 (170×170) — uniform ring:
   * Ellipse 1 full gray track; Ellipses 3/4/5 same inset (~8.29%/7.8%).
   */
  const ARC_SCALE = (100 - 8.29 - 7.8) / 100; // 0.8391

  const data = useMemo(
    () => ({
      labels: donutModel.labels,
      datasets: [
        {
          data: donutModel.data,
          backgroundColor: donutModel.backgroundColor,
          borderWidth: 0,
          hoverBorderWidth: 0,
          borderRadius: 0,
          spacing: 0,
          cutout: "62%",
        },
      ],
    }),
    [donutModel]
  );

  const externalTooltip = useCallback((context) => {
    const { chart, tooltip } = context;
    const el = ensureInventoryTooltipEl(tooltipRef);

    if (tooltip.opacity === 0) {
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
      return;
    }

    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex;
    const categoryIndex =
      dataIndex == null ? -1 : donutModel.realIndex[dataIndex];
    if (categoryIndex == null || categoryIndex < 0) {
      el.style.opacity = "0";
      return;
    }

    const label = categoryLabels[categoryIndex] || "";
    const value = values[categoryIndex] ?? 0;
    const textEl = el.querySelector(".oh-inventory-tooltip__text");
    if (textEl) textEl.textContent = `${label}: ${value}`;

    const anchor = getOutsideTooltipAnchor(chart, tooltip, 10);
    if (!anchor) {
      el.style.opacity = "0";
      return;
    }

    const canvasRect = chart.canvas.getBoundingClientRect();
    const left = canvasRect.left + anchor.x;
    const top = canvasRect.top + anchor.y;

    el.dataset.side = anchor.side;
    el.style.position = "fixed";
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.opacity = "1";
    el.style.pointerEvents = "none";
  }, [donutModel.realIndex, categoryLabels, values]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      radius: `${(ARC_SCALE * 100).toFixed(2)}%`,
      rotation: -90,
      circumference: 360,
      interaction: {
        mode: "nearest",
        intersect: true,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: externalTooltip,
        },
      },
      layout: {
        padding: 0,
      },
    }),
    [externalTooltip]
  );

  // Remount after first successful fetch so hit-testing matches final layout/data.
  const chartKey = hasLoaded
    ? `inv-${inventoryData.inStock}-${inventoryData.lowStock}-${inventoryData.outOfStock}`
    : "inv-pending";

  return (
    <MDBCard
      className="oh-dashboard-card oh-dashboard-card--inventory h-100 shadow-0 border mb-0"
      dir={isArabic ? "rtl" : undefined}
    >
      <MDBCardBody className="oh-dashboard-inventory">
        <div className="oh-dashboard-inventory__top">
          <div className="oh-dashboard-inventory__title-row">
            <MDBCardTitle className="oh-dashboard-card__title oh-dashboard-inventory__title">
              {t("sidebar.inventory")}
            </MDBCardTitle>
            <div className="oh-dashboard-inventory__filter" aria-hidden="true">
              <span className="oh-dashboard-inventory__filter-label">
                {isArabic ? "هذا الأسبوع" : "This Week"}
              </span>
              <ChevronDown
                className="oh-dashboard-inventory__filter-chevron"
                size={12}
                strokeWidth={2}
              />
            </div>
          </div>

          <div className="oh-dashboard-card__legend oh-dashboard-card__legend--top">
            <LegendItem
              color={COLORS.inStock}
              label={labelText.inStock}
              tooltip={`${labelText.inStock}: ${inventoryData.inStock}`}
              square
            />
            <LegendItem
              color={COLORS.lowStock}
              label={labelText.lowStock}
              tooltip={`${labelText.lowStock}: ${inventoryData.lowStock}`}
            />
            <LegendItem
              color={COLORS.outOfStock}
              label={labelText.outOfStock}
              tooltip={`${labelText.outOfStock}: ${inventoryData.outOfStock}`}
            />
            <LegendItem
              color={COLORS.track}
              label={labelText.totalStock}
              tooltip={`${labelText.totalStock}: ${totalStock}`}
            />
          </div>
        </div>

        <div className="oh-dashboard-card__chart oh-dashboard-card__chart--donut">
          <Doughnut
            key={chartKey}
            ref={chartRef}
            data={data}
            options={options}
            plugins={[inventoryTrackPlugin]}
          />
        </div>
      </MDBCardBody>
    </MDBCard>
  );
};

const LegendItem = ({ color, label, tooltip, square = false }) => (
  <MDBTooltip tag="span" title={tooltip} placement="top">
    <span className="oh-dashboard-card__legend-item">
      <span
        className={`oh-dashboard-card__legend-dot${
          square ? " oh-dashboard-card__legend-dot--square" : ""
        }`}
        style={{ backgroundColor: color }}
      />
      <span className="oh-dashboard-card__legend-label">{label}</span>
    </span>
  </MDBTooltip>
);

export default InventoryCard;
